/**
 * Component to Show Report Link
 *
 *
 *
 */
import React, { useState, useEffect } from 'react';
import { Checkbox, Radio } from 'antd';

import './report-link.scss';

import { useTranslation, Card } from 'soxo-bootstrap-core';

import { SCRFilesLink } from '../../../../models';

export default function ReportLink({ billId, displayColumns, callback }) {
    const { t } = useTranslation();

    const [selectedReports, setSelectedReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null); // for radio mode
    const [selectAll, setSelectAll] = useState(false);
    const [patient, setPatient] = useState({ opReg: {}, report: [] });
    const [finalReport, setFinalReport] = useState({});

    const isReportSelected = (id) => {
        return displayColumns ? selectedReports.some((r) => r.report_id === id) : selectedReport?.report_id === id;
    };

    /**
     * The onClick function sets the selected report, calls a callback function with the selected
     * report, and updates the selected report state.
     * @param selectedReport - The `selectedReport` parameter is an object that contains information
     * about a specific report. It likely has properties such as `report_id`, `report_name`,
     * `report_type`, etc. The `onClick` function is a handler function that is called when a report is
     * selected or clicked on. It updates
     */
    const onCheckboxChange = (checked, report) => {
        let updatedSelection = [...selectedReports];

        if (checked) {
            if (!isReportSelected(report.report_id)) {
                updatedSelection.push(report);
            }
        } else {
            updatedSelection = updatedSelection.filter((r) => r.report_id !== report.report_id);
        }

        setSelectedReports(updatedSelection);
        callback(updatedSelection);

        const totalReports = (patient.report?.[0]?.report_details?.length || 0) + (finalReport?.report_id ? 1 : 0);
        setSelectAll(updatedSelection.length === totalReports);
    };

    const handleSelectAllModality = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);

        const reportsToSelect = [];

        if (finalReport?.report_id) {
            reportsToSelect.push({ ...finalReport });
        }

        if (patient.report?.[0]?.report_details?.length) {
            patient.report[0].report_details.forEach((result) => {
                reportsToSelect.push({
                    report_path: result.scrreptd_filename,
                    type: 'sub',
                    report_type: 'RPT',
                    report_id: result.scrreptd_id,
                });
            });
        }

        setSelectedReports(checked ? reportsToSelect : []);
        callback(checked ? reportsToSelect : []);
    };

    const onClick = (report) => {
        setSelectedReport(report);
        callback(report);
    };

    /* The `useEffect` hook is used to perform side effects in a functional component. In this case, it
    is used to call the `getReports` function when the component is mounted. */
    useEffect(async () => {
        getReports();
    }, []);

    /**
     * Getting reports
     *
     */
    async function getReports() {
        //Load report with billId
        const result = await SCRFilesLink.getReportDetails(billId);

        // Extract corporate email
        const corporateEmail = result.data?.corporate_email || '';

        // Set patient data
        setPatient(result.data);
        // Select the first report if available
        if (result.data.report && result.data.report[0] && result.data.report[0].report_details) {
            const selected = {
                report_type: 'RPT',
                report_path: result.data.report[0].scrrept_filename,
                report_id: result.data.report[0].scrrept_id,
                type: 'final',
                report_not_found: result.data.report[0].report_not_found,
            };

            setFinalReport(selected);

            // Optionally, pass the corporate email along with selected report
            callback({ selectedReports: [selected], corporateEmail });
        } else {
            // If no report, still pass corporate email
            callback({ selectedReports: [], corporateEmail });
        }
    }

    return (
        <div className="card report-link card-shadow">
            <div className="card-title" style={{ width: '100%' }}>
                <h3>{t('REPORTS')}</h3>
            </div>

            {/* Checkbox: Select All Option */}
            {displayColumns && (
                <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Checkbox onChange={handleSelectAllModality} checked={selectAll}>
                        {t('Select All')}
                    </Checkbox>
                </div>
            )}

            <div className="report-tabs-wrapper">
                {/* Final Report */}
                {finalReport?.report_id && finalReport?.report_not_found && (
                    <div className="report-tab">
                        <div
                            className={`test-name card card-shadow ${isReportSelected(finalReport.report_id) ? 'active' : ''}`}
                            style={{ backgroundColor: isReportSelected(finalReport.report_id) ? '#e6f7ff' : '' }}
                        >
                            {displayColumns ? (
                                <Checkbox
                                    checked={isReportSelected(finalReport.report_id)}
                                    onChange={(e) => onCheckboxChange(e.target.checked, finalReport)}
                                >
                                    <p>{t('Final Report')}</p>
                                </Checkbox>
                            ) : (
                                <div
                                    className={`test-name card card-shadow ${isReportSelected(finalReport.report_id) ? 'active' : ''}`}
                                    onClick={() => onClick(finalReport)}
                                >
                                    <p>{t('Final Report')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {/* Final Report Tab Ends */}

                {/* Sub Reports */}
                {patient.report?.[0]?.report_details?.map((result, index) => {
                    const reportObj = {
                        report_path: result.scrreptd_filename,
                        type: 'sub',
                        report_type: 'RPT',
                        report_id: result.scrreptd_id,
                    };

                    const isSelected = isReportSelected(reportObj.report_id);

                    return (
                        <div className="report-tab" key={index}>
                            {result?.report_not_found && (
                                <div
                                    className={`test-name card card-shadow ${isSelected ? 'active' : ''}`}
                                    style={{ backgroundColor: isSelected ? '#e6f7ff' : '' }}
                                >
                                    {displayColumns ? (
                                        <Checkbox checked={isSelected} onChange={(e) => onCheckboxChange(e.target.checked, reportObj)}>
                                            {index + 1}. {result.report_master_details.srepmd_desc}
                                        </Checkbox>
                                    ) : (
                                        <div
                                            className={`test-name card card-shadow ${isSelected ? 'active' : ''}`}
                                            onClick={() => onClick(reportObj)}
                                        >
                                            {/* <Radio
                                        checked={isSelected}
                                        onChange={() => onRadioChange(reportObj)}
                                    > */}
                                            {index + 1}. {result.report_master_details.srepmd_desc}
                                            {/* </Radio> */}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* Sub Report Starts */}
        </div>
    );
}
